<template>
  <c-input-select
    ref="searchSelectRef"
    :model-value="null"
    class="header-student-search tw-w-72"
    :options="options"
    option-label="name"
    use-input
    hide-dropdown-icon
    dense
    :input-debounce="300"
    :loading="isLoading"
    placeholder="Zoek leerling"
    @filter="onFilter"
    @update:model-value="onSelect"
  >
    <template #prepend>
      <q-icon :name="toClassicIcon(searchIcon)" size="16px" />
    </template>

    <template #option="{ itemProps, opt }">
      <c-select-option :item-props="itemProps">
        <template #avatar>
          <c-avatar :name="opt.name" :src="opt.avatar" />
        </template>

        <c-highlight-text :text="opt.name" :needle="needle" />

        <template #caption>
          <c-highlight-text :text="subtitleFor(opt)" :needle="needle" />
        </template>
      </c-select-option>
    </template>

    <template #no-option>
      <div class="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500">
        Geen leerlingen gevonden
      </div>

      <div
        v-if="scopedToTeacher"
        class="tw-border-t tw-px-4 tw-py-3 tw-text-xs tw-text-gray-500"
      >
        {{ SCOPED_NOTE }}
      </div>
    </template>

    <template #after-options>
      <div
        v-if="scopedToTeacher"
        class="tw-border-t tw-px-4 tw-py-3 tw-text-xs tw-text-gray-500"
      >
        {{ SCOPED_NOTE }}
      </div>
    </template>
  </c-input-select>
</template>

<script setup lang="ts">
import { searchIcon, toClassicIcon } from "common/defaultIcons";
import { fetchStudentSearchResults } from "shared/api";
import { SIDEBARS } from "shared/constants";
import { useSidebar } from "shared/composables/useSidebar";
import type { StudentSearchResult } from "shared/types";
import { onUnmounted, ref, useTemplateRef } from "vue";

const MIN_SEARCH_LENGTH = 2;
const SCOPED_NOTE =
  "Er worden uitsluitend leerlingen getoond waar je aan lesgeeft of mentor van bent.";

const { openSidebar } = useSidebar();

const searchSelectRef = useTemplateRef<{
  selectRef: {
    blur: () => void;
    updateInputValue: (value: string, noFilter?: boolean) => void;
  } | null;
}>("searchSelectRef");

const options = ref<StudentSearchResult[]>([]);
const needle = ref("");
const scopedToTeacher = ref(false);
const isLoading = ref(false);
const abortController = ref<AbortController | null>(null);

const subtitleFor = (student: StudentSearchResult): string =>
  [student.studentNumber, student.mentorClass]
    .filter((value) => value !== null && value !== "")
    .join(" · ");

const onFilter = async (
  inputValue: string,
  update: (callback: () => void) => void,
  abort: () => void,
) => {
  const search = inputValue.trim();

  // No search yet (e.g. the input just got focus): don't open the dropdown
  // at all, instead of showing an empty "no results" message.
  if (search.length < MIN_SEARCH_LENGTH) {
    abortController.value?.abort();
    isLoading.value = false;
    options.value = [];
    needle.value = "";
    abort();

    return;
  }

  abortController.value?.abort();
  const controller = new AbortController();
  abortController.value = controller;
  isLoading.value = true;

  try {
    const response = await fetchStudentSearchResults(search, controller.signal);

    update(() => {
      options.value = response.results;
      scopedToTeacher.value = response.scopedToTeacher;
      needle.value = search;
    });
  } catch {
    if (controller.signal.aborted) {
      return;
    }

    update(() => {
      options.value = [];
    });
  } finally {
    if (abortController.value === controller) {
      isLoading.value = false;
    }
  }
};

const onSelect = (student: StudentSearchResult | null) => {
  if (student === null) {
    return;
  }

  openSidebar(SIDEBARS.STUDENT, student.id);

  const select = searchSelectRef.value?.selectRef;
  select?.updateInputValue("", true);
  select?.blur();
  options.value = [];
  needle.value = "";
};

onUnmounted(() => {
  abortController.value?.abort();
});
</script>
